import React from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

// images
import SuccessIcon from '../../images/SuccessIcon.svg?react';
import WarningIcon from '../../images/WarningIcon.svg?react';

// components
import BasicButton from '../buttons/basicButton';
import { ConfirmModalTypes } from './confirmModal';

interface StyledProps {
    type?: ConfirmModalTypes;
}

interface ConfirmPromptProps extends StyledProps {
    message: string;
    description?: string;
    confirm?: string;
    abort?: string;
    abortHandler?: () => void;
    confirmHandler?: () => void;
    style?: React.CSSProperties;
    showWarningButton?: boolean;
    showOutlinedButton?: boolean;
}

const ConfirmPrompt: React.FC<React.PropsWithChildren<ConfirmPromptProps>> = ({
    type,
    message,
    description,
    confirm = 'Continue',
    abort = 'Cancel',
    abortHandler,
    confirmHandler,
    style,
    showWarningButton = true,
    showOutlinedButton = false,
}) => {
    const imageType = {
        warning: <WarningIcon />,
        success: <SuccessIcon />,
    };

    return (
        <ModalContainer style={{ ...style }}>
            {type && imageType[type]}
            <Content type={type}>
                {message && <Message>{message}</Message>}
                {description && <Description>{description}</Description>}
            </Content>
            <Buttons>
                {abortHandler !== undefined && (
                    <BasicButton
                        isPrimary={false}
                        name={abort}
                        action={abortHandler}
                        style={{ width: '100%' }}
                    />
                )}
                <BasicButton
                    isPrimary={!showOutlinedButton}
                    name={confirm}
                    action={confirmHandler}
                    style={{ width: '100%' }}
                    isWarning={type === 'warning' && showWarningButton}
                    isOutlined={showOutlinedButton}
                />
            </Buttons>
        </ModalContainer>
    );
};

const ModalContainer = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
}));

const Content = styled('div').withConfig({
    shouldForwardProp: (prop) => prop !== 'type',
})<StyledProps>((props) => ({
    marginTop: aampeTheme.spacing(props.type ? 3 : 0),
}));

const Message = styled.p(() => ({
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '28px',
}));

const Description = styled.p(() => ({
    marginTop: aampeTheme.spacing(1),
    textAlign: 'center',
    fontSize: '14px',
    color: aampeTheme.colors.neutral09,
}));

const Buttons = styled.div(() => ({
    marginTop: aampeTheme.spacing(4),
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: aampeTheme.spacing(2),
    alignItems: 'center',
}));

export default ConfirmPrompt;
